import { useState } from 'react'
import { useRegistrarFato, useTiposDeFato } from '../../../api/queries/ordensDeServico'
import { Botao } from '../../../components/Botao'
import { Dialogo } from '../../../components/Dialogo'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { CampoDeSelecao, CampoDeTexto, CampoDeTextoLongo } from '../../../components/campos'
import type { DetalheDaOs, MeioFormal } from '../../../types/ordemDeServico'
import { OS } from '../textosDaOs'

const MEIOS = Object.keys(OS.meios) as MeioFormal[]

/**
 * Registra um ato datado da ordem.
 *
 * **O catálogo de atos vem do servidor, nunca de uma lista aqui.** A CONTRATANTE pode criar
 * documento por ato administrativo (6.23, p.44); uma cópia no navegador ficaria muda justamente no
 * mês em que a decisão passa a valer.
 *
 * **O que já foi registrado some da lista quando o ato é único por ordem.** É a mesma regra que o
 * servidor aplica (`FATO_JA_REGISTRADO`), antecipada para não oferecer o que ele vai recusar — e
 * repare que ela não substitui a recusa: quem tiver a tela aberta desde antes continua sendo
 * barrado lá.
 *
 * **A data mínima é a emissão da ordem.** Fato anterior à emissão é recusado pelo servidor
 * (`FATO_ANTERIOR_A_EMISSAO`), e um prazo contado de uma data que precede o documento não é prazo.
 *
 * @param props a ordem e como fechar o diálogo
 * @returns o diálogo do registro
 */
export function RegistrarFato({ ordem, aoFechar }: { ordem: DetalheDaOs; aoFechar: () => void }) {
  const { data: tipos, isPending } = useTiposDeFato()
  const registrar = useRegistrarFato()
  const [tipo, definirTipo] = useState('')
  const [ocorridoEm, definirOcorridoEm] = useState('')
  const [meio, definirMeio] = useState<MeioFormal | ''>('')
  const [referencia, definirReferencia] = useState('')
  const [observacao, definirObservacao] = useState('')

  const jaRegistrados = new Set(ordem.fatos.map((fato) => fato.tipo))
  const disponiveis = (tipos ?? []).filter(
    (candidato) => candidato.ativo && !(candidato.unicoPorOs && jaRegistrados.has(candidato.codigo)),
  )
  const escolhido = disponiveis.find((candidato) => candidato.codigo === tipo)
  const valido = tipo.length > 0 && /^\d{4}-\d{2}-\d{2}$/.test(ocorridoEm)

  return (
    <Dialogo titulo={OS.andamento.registrar} aoFechar={aoFechar}>
      {isPending ? (
        <Esqueleto linhas={4} />
      ) : (
        <div className="space-y-4">
          <CampoDeSelecao
            rotulo="O que aconteceu"
            ajuda={escolhido ? `${escolhido.descricao} (${escolhido.fonteDoTr})` : undefined}
            value={tipo}
            onChange={(evento) => definirTipo(evento.target.value)}
          >
            <option value="">Escolha o ato</option>
            {disponiveis.map((candidato) => (
              <option key={candidato.codigo} value={candidato.codigo}>
                {candidato.rotulo}
              </option>
            ))}
          </CampoDeSelecao>

          <CampoDeTexto
            rotulo="Quando aconteceu"
            ajuda="O dia do ato, não o dia em que você está registrando. É desta data que o contrato conta prazo."
            type="date"
            min={ordem.emitidaEm}
            value={ocorridoEm}
            onChange={(evento) => definirOcorridoEm(evento.target.value)}
          />

          <CampoDeSelecao
            rotulo={OS.andamento.meio}
            ajuda="O meio formal que prova a data (item 6.47, p.48)."
            value={meio}
            onChange={(evento) => definirMeio(evento.target.value as MeioFormal | '')}
          >
            <option value="">Não informado</option>
            {MEIOS.map((codigo) => (
              <option key={codigo} value={codigo}>
                {OS.meios[codigo]}
              </option>
            ))}
          </CampoDeSelecao>

          <CampoDeTexto
            rotulo={OS.andamento.referencia}
            ajuda="Onde o documento está: número do ofício, do processo, do e-mail."
            value={referencia}
            onChange={(evento) => definirReferencia(evento.target.value)}
          />

          <CampoDeTextoLongo
            rotulo="Observação"
            rows={2}
            value={observacao}
            onChange={(evento) => definirObservacao(evento.target.value)}
          />

          {registrar.isError && <AvisoDeErro erro={registrar.error} />}

          <div className="flex flex-wrap gap-2">
            <Botao
              aparencia="primario"
              disabled={!valido || registrar.isPending}
              onClick={() =>
                registrar.mutate(
                  {
                    id: ordem.id,
                    tipo,
                    ocorridoEm,
                    meio,
                    referencia: referencia.trim(),
                    observacao: observacao.trim(),
                  },
                  { onSuccess: aoFechar },
                )
              }
            >
              Registrar
            </Botao>
            <Botao onClick={aoFechar}>Cancelar</Botao>
          </div>
        </div>
      )}
    </Dialogo>
  )
}
